import type { NonNullableFormBuilder } from '@angular/forms';
import type { OmitDeep, SetOptional } from 'type-fest';
import { CountriesWhitelistFormComponent } from '../../../../common/components/forms/countries-whitelist-form/countries-whitelist-form.component';
import { NoteFormComponent } from '../../../../common/components/forms/note-form/note-form.component';
import type { PreferencesService } from '../../../../common/services/preferences.service';
import { DriverCardFormComponent } from '../../driver-form/driver-card-form/driver-card-form.component';
import { DriverClearCriminalRecordCertificateFormComponent } from '../../driver-form/driver-clear-criminal-record-certificate-form/driver-clear-criminal-record-certificate-form.component';
import { DriverDataFormComponent } from '../../driver-form/driver-data-form/driver-data-form.component';
import { DriverDriversLicenseFormComponent } from '../../driver-form/driver-drivers-license-form/driver-drivers-license-form.component';
import { DriverEmploymentConditionsFormComponent } from '../../driver-form/driver-employment-conditions-form/driver-employment-conditions-form.component';
import { DriverIdentityDocumentsFormComponent } from '../../driver-form/driver-identity-documents-form/driver-identity-documents-form.component';
import { DriverPermissionsFormComponent } from '../../driver-form/driver-permissions-form/driver-permissions-form.component';
import { DriverSkillsFormComponent } from '../../driver-form/driver-skills-form/driver-skills-form.component';
import { DriverVisasFormComponent } from '../../driver-form/driver-visas-form/driver-visas-form.component';
import type { Driver } from '../interfaces/driver';

export type DriverFormGroup = ReturnType<typeof DriverFormHelper.buildForm>;
export type DriverForm = ReturnType<DriverFormGroup['getRawValue']>;

export const DRIVER_UTILITY_KEYS = [
  'driversLicenseAndProfessionalQualifications.selectedCategories',
  'identityDocuments.hasRequiredDocument',
] as const;

export type DriverFormDto = SetOptional<
  OmitDeep<DriverForm, (typeof DRIVER_UTILITY_KEYS)[number]>,
  'driverCard'
>;

export class DriverFormHelper {
  public static fromDto(dto: Driver): DriverForm {
    return {
      ...dto,
      driverData: {
        ...dto.driverData,
        assignedBase: dto.driverData.assignedBase?.uuid ?? null,
      },
      employmentConditions: {
        ...dto.employmentConditions,
        mileageRate: dto.employmentConditions
          .mileageRate as DriverForm['employmentConditions']['mileageRate'],
        dailyRate: dto.employmentConditions
          .dailyRate as DriverForm['employmentConditions']['dailyRate'],
        baseRate: dto.employmentConditions
          .baseRate as DriverForm['employmentConditions']['baseRate'],
        roundTripRate: dto.employmentConditions
          .roundTripRate as DriverForm['employmentConditions']['roundTripRate'],
        scans: dto.employmentConditions
          .scans as DriverForm['employmentConditions']['scans'],
      },
      driversLicenseAndProfessionalQualifications: {
        ...dto.driversLicenseAndProfessionalQualifications,
        selectedCategories:
          dto.driversLicenseAndProfessionalQualifications.categories.map(
            (category) => category.category,
          ),
        scans: dto.driversLicenseAndProfessionalQualifications
          .scans as DriverForm['driversLicenseAndProfessionalQualifications']['scans'],
      },
      driverCard: dto.driverCard as DriverForm['driverCard'],
      identityDocuments: {
        passport: dto.identityDocuments
          .passport as DriverForm['identityDocuments']['passport'],
        identityDocument: dto.identityDocuments
          .identityDocument as DriverForm['identityDocuments']['identityDocument'],
        residenceCard: dto.identityDocuments
          .residenceCard as DriverForm['identityDocuments']['residenceCard'],
        polishCard: dto.identityDocuments
          .polishCard as DriverForm['identityDocuments']['polishCard'],
      },
      countries: dto.countries as DriverForm['countries'],
      clearCriminalRecordCertificate: {
        ...dto.clearCriminalRecordCertificate,
        scans: dto.clearCriminalRecordCertificate
          .scans as DriverForm['clearCriminalRecordCertificate']['scans'],
      },
      visas: dto.visas.map((visa) => ({
        ...visa,
        scans: visa.scans as DriverForm['visas'][number]['scans'],
      })),
      permissions: dto.permissions.map((permission) => ({
        ...permission,
        type: permission.type.uuid,
        scans: permission.scans as DriverForm['permissions'][number]['scans'],
      })),
      note: {
        content: dto.note?.content ?? null,
      },
    };
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      driverData: DriverDataFormComponent.buildForm(fb),
      employmentConditions: DriverEmploymentConditionsFormComponent.buildForm(
        fb,
        preferencesService,
      ),
      skills: DriverSkillsFormComponent.buildForm(fb),
      countries: CountriesWhitelistFormComponent.buildForm(fb),
      note: NoteFormComponent.buildForm(fb),
      driversLicenseAndProfessionalQualifications:
        DriverDriversLicenseFormComponent.buildForm(fb),
      driverCard: DriverCardFormComponent.buildForm(fb),
      identityDocuments: DriverIdentityDocumentsFormComponent.buildForm(fb),
      clearCriminalRecordCertificate:
        DriverClearCriminalRecordCertificateFormComponent.buildForm(fb),
      visas: DriverVisasFormComponent.buildForm(fb),
      permissions: DriverPermissionsFormComponent.buildForm(fb),
    });
  }
}
